import React from "react";

export const templates = [
  {
    id: "app-usability",
    name: "App Usability & Interface",
    icon: (props) => (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" {...props}>
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
      </svg>
    ),
    question: "How would you rate your experience using the DingAI app?",
    options: [
      "Very easy to use and intuitive",
      "Generally good but some confusion",
      "Somewhat difficult to navigate",
      "Very confusing and needs improvement"
    ]
  },
  {
    id: "feature-requests",
    name: "Feature Requests & Improvements",
    icon: (props) => (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" {...props}>
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4" />
      </svg>
    ),
    question: "What new features would you like to see in the DingAI app?",
    options: [
      "More survey customization options",
      "Better analytics and reporting",
      "Integration with more platforms",
      "Improved AI insights and recommendations"
    ]
  },
  {
    id: "app-performance",
    name: "App Performance & Reliability",
    icon: (props) => (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" {...props}>
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    ),
    question: "How would you describe the performance of the DingAI app?",
    options: [
      "Very fast and responsive",
      "Generally good performance",
      "Sometimes slow or laggy",
      "Frequently crashes or freezes"
    ]
  },
  {
    id: "user-support",
    name: "User Support & Documentation",
    icon: (props) => (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" {...props}>
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    question: "What type of support would help you use DingAI better?",
    options: [
      "Better in-app tutorials and guides",
      "More detailed documentation",
      "Video tutorials and examples",
      "Live chat or email support"
    ]
  },
  {
    id: "overall-satisfaction",
    name: "Overall App Satisfaction",
    icon: (props) => (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" {...props}>
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1m-6 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    question: "How satisfied are you with the DingAI app overall?",
    options: [
      "Very satisfied - exceeds expectations",
      "Satisfied - meets my needs",
      "Somewhat satisfied - has room for improvement",
      "Not satisfied - needs significant changes"
    ]
  }
];

const MultipleChoiceSurveyTemplates = ({ onSelectTemplate, selectedTemplateId }) => {
  const handleSelectTemplate = (template) => {
    onSelectTemplate(template);
  };

  return (
    <div className="rounded-lg border bg-white shadow-sm">
      <div className="flex flex-col space-y-1.5 p-6">
        <h3 className="font-medium text-gray-900">Start from a Template</h3>
        <p className="text-sm text-gray-500">
          Choose from proven templates designed to collect feedback about the DingAI app
        </p>
        <div className="mt-2 flex items-center p-2 bg-blue-50 rounded-md border border-blue-100">
          <span className="mr-2 text-[#2962FF] font-bold">💡</span>
          <span className="text-sm text-gray-700">You can either start from a template or write your own question below.</span>
        </div>
      </div>
      <div className="p-6 pt-0 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {templates.map((template) => (
          <div 
            key={template.id} 
            className={`border rounded-lg transition-all cursor-pointer h-full flex flex-col ${
              selectedTemplateId === template.id 
                ? "ring-2 ring-[#2962FF] border-[#2962FF] shadow-md" 
                : "hover:border-[#2962FF]/40 hover:shadow"
            }`}
            onClick={() => handleSelectTemplate(template)}
          >
            <div className="p-4 pb-0">
              <div className="flex items-center gap-2">
                <div className="p-1.5 rounded-md bg-gray-100 text-gray-600">
                  <template.icon className="h-4 w-4" />
                </div>
                <h3 className="font-medium text-gray-900">{template.name}</h3>
              </div>
            </div>
            <div className="p-4 flex-grow">
              <p className="text-xs font-medium mb-2">{template.question}</p>
              <ul className="text-xs text-gray-600 space-y-1 pl-[0.9rem] list-disc">
                {template.options.map((option, index) => (
                  <li key={index} className="text-xs">
                    {option}
                  </li>
                ))}
                <li key="other" className="text-xs">Other</li>
              </ul>
            </div>
            <div className="p-4 pt-2 mt-auto">
              <button 
                type="button" 
                className={`w-full py-1 px-4 rounded text-sm border ${
                  selectedTemplateId === template.id
                    ? "bg-[#2962FF]/10 border-[#2962FF] text-[#2962FF]"
                    : "border-[#2962FF]/60 text-[#2962FF] hover:bg-[#2962FF]/5"
                }`}
                onClick={(e) => {
                  e.stopPropagation(); // Prevent triggering the card's onClick
                  handleSelectTemplate(template);
                }}
              >
                Use this template
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default MultipleChoiceSurveyTemplates;
